/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */
import React, { ReactNode } from 'react'

import { UserInfo } from '@/providers/auth-types'
import { Company } from '@/types/dashboard-types'

import AssetSeverityIcon from './AlertSeverityIcon'
import AssetNameCol from './AssetNameCol'
import BasicPropCol from './BasicPropCol'
import DateCol from './DateCol'
import DefaultCol from './DefaultCol'
import DoorMpCol from './DoorMpCol'
import DoorMpSummaryCol from './DoorMpSummaryCol'
import FridgePropCol from './FridgePropCol'
import GatewayIcon from './GatewayIcon'
import IdleTimeCol from './IdleTimeCol'
import LocationCol from './LocationCol'
import OfflineSinceCol from './OfflineSinceCol'
import PowerStateIcon from './PowerStateIcon'
import ServiceCol from './ServiceCol'
import TempMpCol from './TempMpCol'
import TempMpSummaryCol from './TempMpSummaryCol'
import UmdCol from './UmdCol'
import {
  estimateColumnWidth,
  getBearingDirection,
  getDynamicDoorMpColName,
  getDynamicTempMpColName,
  isDynamicDoorMpCol,
  isDynamicTempMpCol,
} from './utils'
import VspCol from './VspCol'

import { AssetStateSummary } from '../types'

export const formatCell = (
  column: string,
  value: number | string | Date | null,
  asset: AssetStateSummary,
  user: UserInfo | null,
  company: Company | undefined
): React.ReactNode => {
  // Dynamic Temp cols
  if (isDynamicTempMpCol(column)) {
    const tempMp = asset.tempMps.find((t) => getDynamicTempMpColName(t.name) === column)
    if (!tempMp) {
      return <></>
    }
    return (
      <TempMpCol
        value={tempMp.temperatureCelsius}
        gatewayOn={asset.gatewayOn}
        tagOnline={tempMp.tagOnline}
        tagSampleTime={tempMp.tagSampleTime}
        temperatureUnit={user?.defaultTemperatureUnits}
      />
    )
  }

  if (isDynamicDoorMpCol(column)) {
    const doorMp = asset.doorMps.find((d) => getDynamicDoorMpColName(d.name) === column)
    if (!doorMp) {
      return <></>
    }
    return (
      <DoorMpCol
        value={doorMp.connected}
        gatewayOn={asset.gatewayOn}
        onlineSampleTime={doorMp.onlineSampleTime}
        tagOnline={doorMp.tagOnline}
        tagSampleTime={doorMp.tagSampleTime}
      />
    )
  }

  switch (column) {
    case 'name':
      return <AssetNameCol name={asset.name} umd={asset.umd} isShared={!!asset.ccpShared} />
    case 'alertSeverity':
      return <AssetSeverityIcon severity={asset.highestAlertSeverity} />
    case 'powerOn':
      return (
        <PowerStateIcon
          powerState={asset.powerState}
          powerStateSampleTime={asset.powerStateSampleTime}
          maintenanceModeEventSampleTime={asset.maintenanceModeEventSampleTime}
          maintenanceMode={asset.maintenanceMode}
        />
      )
    case 'gatewayOn':
      return (
        <GatewayIcon
          gatewayOn={asset.gatewayOn}
          addressShort={asset.addressShort}
          geofence={asset.geofence}
          umdBatteryVoltage={asset.umdBatteryVoltage}
          umdSampleTime={asset.umdSampleTime}
          umdSleepMode={asset.umdSleepMode}
        />
      )
    case 'addressShort':
    case 'street':
    case 'city':
    case 'postcode':
    case 'state':
    case 'country':
    case 'geofence':
      return (
        <LocationCol
          value={value as string}
          gatewayOn={asset.gatewayOn}
          locationSampleTime={asset.locationSampleTime}
        />
      )
    case 'bearingDegrees':
      return (
        <LocationCol
          value={getBearingDirection(value as number)}
          gatewayOn={asset.gatewayOn}
          locationSampleTime={asset.locationSampleTime}
        />
      )
    case 'airControl':
    case 'operation':
    case 'powerMode':
    case 'powerSource':
    case 'fridgeProfile':
    case 'zone1Mode':
    case 'zone2Mode':
    case 'zone3Mode':
    case 'engineSpeed':
      return (
        <FridgePropCol
          value={value as string}
          fridgeOnline={asset.fridgeOnline}
          fridgeSampleTime={asset.fridgeSampleTime}
          powerState={asset.powerState}
          type="String"
        />
      )
    case 'ambientTemperatureCelsius':
    case 'zone1SupplyAir':
    case 'zone1ReturnAir':
    case 'zone2SupplyAir':
    case 'zone2ReturnAir':
    case 'zone3SupplyAir':
    case 'zone3ReturnAir':
      return (
        <FridgePropCol
          value={value as number}
          fridgeOnline={asset.fridgeOnline}
          fridgeSampleTime={asset.fridgeSampleTime}
          powerState={asset.powerState}
          type="Temperature"
          temperatureUnit={user?.defaultTemperatureUnits}
        />
      )
    case 'batteryVolts':
    case 'defrostInterval':
      return (
        <FridgePropCol
          value={value as number}
          fridgeOnline={asset.fridgeOnline}
          fridgeSampleTime={asset.fridgeSampleTime}
          powerState={asset.powerState}
          type="Number"
          temperatureUnit={user?.defaultTemperatureUnits}
        />
      )
    case 'zone1SetPoint':
    case 'zone2SetPoint':
    case 'zone3SetPoint':
      return (
        <FridgePropCol
          value={value as number}
          fridgeOnline={asset.fridgeOnline}
          fridgeSampleTime={asset.fridgeSampleTime}
          powerState={asset.powerState}
          type="Setpoint"
          temperatureUnit={user?.defaultTemperatureUnits}
        />
      )
    case 'fuelPercent':
      return (
        <BasicPropCol
          value={value as number}
          sampleTime={asset.fridgeFuelSampleTime}
          moduleOn={asset.fridgeFuelOnline}
          gatewayOn={asset.gatewayOn}
        />
      )
    case 'electricHours':
    case 'dieselHours':
    case 'engineHours':
    case 'totalHours':
    case 'compressorHours':
      return (
        <BasicPropCol
          value={value as number}
          sampleTime={asset.fridgeEngineHoursSampleTime}
          moduleOn={asset.fridgeEngineHoursOnline}
          gatewayOn={asset.gatewayOn}
        />
      )
    case 'speedKmsHr':
      return (
        <BasicPropCol
          value={value as number}
          sampleTime={asset.avgSpeedSampleTime}
          moduleOn={asset.locationOnline}
          gatewayOn={asset.gatewayOn}
        />
      )
    case 'maxSpeedKmsHr':
      return (
        <BasicPropCol
          value={value as number}
          sampleTime={asset.maxSpeedSampleTime}
          moduleOn={asset.locationOnline}
          gatewayOn={asset.gatewayOn}
        />
      )
    case 'calibrationDays':
    case 'dieselHourstillnextservice':
      return <ServiceCol value={value as number} />
    case 'umd':
      return (
        <UmdCol value={value as string} provisionedDate={asset.provisionedDate} installedDate={asset.installedDate} />
      )
    case 'stopSampleTime':
      return <IdleTimeCol value={value as string} idleTime={asset.idleTime} />
    case 'offlineSince':
      return <OfflineSinceCol gatewayOn={asset.gatewayOn} addressSampleTime={asset.addressSampleTime} />
    case 'despatchDate':
    case 'deliveryDate':
      return <DateCol value={value as Date} />
    case 'tempMpSummary':
      return <TempMpSummaryCol tempMps={asset.tempMps} />
    case 'doorMpSummary':
      return <DoorMpSummaryCol doorMps={asset.doorMps} />
    case 'zone1VirtualSetpoint':
    case 'zone2VirtualSetpoint':
    case 'zone3VirtualSetpoint':
      return (
        <VspCol
          value={value as number}
          reeferInterfaceTypeId={asset.reeferInterfaceTypeId}
          virtualSetpointAccessTypeId={company?.virtualSetpointAccessTypeId}
          temperatureUnit={user?.defaultTemperatureUnits}
        />
      )
    case 'lastKnownAddress':
      return <DefaultCol value={asset.addressShort as string} />
    case 'lastKnownGeofence':
      return <DefaultCol value={asset.geofence as string} />
    default:
      return <DefaultCol value={value as string} />
  }
}

export const getColumnWidth = (column: string, title: string = ''): number => {
  // handle dynamic cols
  if (isDynamicDoorMpCol(column) || isDynamicTempMpCol(column)) {
    return estimateColumnWidth(title)
  }

  switch (column) {
    case 'asset':
      return 320
    case 'addressShort':
    case 'lastKnownAddress':
      return 350
    case 'comments':
      return 180
    case 'customer':
    case 'loadSummary':
    case 'despatchDate':
    case 'originLocation':
    case 'destination':
    case 'deliveryDate':
    case 'driverName':
    case 'street':
    case 'lastKnownGeofence':
    case 'geofence':
    case 'calibrationDays':
    case 'offlineSince':
    case 'fridgeProfile':
    case 'fridgeModel':
      return 140
    case 'city':
    case 'country':
    case 'postcode':
    case 'stopSampleTime':
    case 'dieselHourstillnextservice':
    case 'fridgeMake':
      return 130
    default:
      return 120
  }
}

export const getColOffset = (column: string) => {
  switch (column) {
    case 'asset':
      return 0
    default:
      return undefined
  }
}

export const getDefaultSortingIcon = (_column: string): ReactNode => {
  return <></>
}
